import type { Application, Route, Driver } from '../types';
import type { Contractor, Carrier } from '../store/api/referencesApi';

function formatApplicationDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function routeDirectionLabel(r: Route): string {
  const from = r.loadingCity?.trim();
  const to = r.unloadingCity?.trim();
  if (from && to) return `${from} → ${to}`;
  const la = r.loadingAddress?.trim();
  const ua = r.unloadingAddress?.trim();
  if (la || ua) return [la, ua].filter(Boolean).join(' → ');
  return '—';
}

/** Тип сущности в глобальном поиске (как в ТЗ). */
export type GlobalSearchEntityType = 'application' | 'route' | 'counterparty' | 'carrier' | 'driver';

export interface GlobalSearchHit {
  id: string;
  type: GlobalSearchEntityType;
  title: string;
  subtitle?: string;
  description?: string;
}

/** Максимум результатов в выпадающем списке (все типы вместе). */
export const MAX_GLOBAL_SEARCH_RESULTS = 10;

/** Не запрашивать справочники (контрагенты, перевозчики, водители), если запрос короче. */
export const MIN_QUERY_LENGTH_FOR_REFERENCES = 2;

const PRIORITY: Record<GlobalSearchEntityType, number> = {
  application: 0,
  route: 1,
  counterparty: 2,
  carrier: 3,
  driver: 4,
};

const APP_NUMBER_RE = /^\d{4}-\d+$/i;

export function isNumericIdQuery(q: string): boolean {
  return /^\d+$/.test(q.trim());
}

export function isApplicationNumberQuery(q: string): boolean {
  return APP_NUMBER_RE.test(q.trim());
}

/**
 * Дедупликация, приоритет точного совпадения title с запросом (без учёта регистра),
 * затем по типу (application → … → driver), не более MAX_GLOBAL_SEARCH_RESULTS записей.
 */
function finalizeHits(hits: GlobalSearchHit[], queryForExactMatch: string): GlobalSearchHit[] {
  const q = queryForExactMatch.trim().toLowerCase();
  const indexed = hits.map((h, i) => ({ h, i }));
  indexed.sort((a, b) => {
    const exactA = q.length > 0 && a.h.title.trim().toLowerCase() === q ? 0 : 1;
    const exactB = q.length > 0 && b.h.title.trim().toLowerCase() === q ? 0 : 1;
    if (exactA !== exactB) return exactA - exactB;
    const pa = PRIORITY[a.h.type];
    const pb = PRIORITY[b.h.type];
    if (pa !== pb) return pa - pb;
    return a.i - b.i;
  });
  const seen = new Set<string>();
  const out: GlobalSearchHit[] = [];
  for (const { h } of indexed) {
    const k = `${h.type}:${h.id}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(h);
    if (out.length >= MAX_GLOBAL_SEARCH_RESULTS) break;
  }
  return out;
}

function textIn(q: string, ...parts: (string | undefined | null)[]): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return false;
  return parts.some((p) => p && String(p).toLowerCase().includes(n));
}

function appHit(a: Application): GlobalSearchHit {
  const num = a.internalNumber || a.externalNumber || String(a.id);
  const clientName = a.client?.name?.trim() || '—';
  const dateStr = formatApplicationDate(a.applicationDate);
  const subtitle = dateStr ? `${clientName} · ${dateStr}` : clientName;
  return {
    id: String(a.id),
    type: 'application',
    title: `Заявка #${num}`,
    subtitle,
    description: [a.loadingAddress, a.unloadingAddress].filter(Boolean).join(' → ') || undefined,
  };
}

function routeHit(r: Route): GlobalSearchHit {
  const label = r.number?.trim() ? r.number : `#${r.id}`;
  const clientName = r.client?.name?.trim() || '—';
  const direction = routeDirectionLabel(r);
  return {
    id: String(r.id),
    type: 'route',
    title: `Маршрут ${label}`,
    subtitle: `${clientName} · ${direction}`,
    description: [r.loadingAddress, r.unloadingAddress].filter(Boolean).join(' → ') || undefined,
  };
}

function contractorHit(c: Contractor): GlobalSearchHit {
  return {
    id: String(c.id),
    type: 'counterparty',
    title: c.name,
    subtitle: c.roles?.join(', ') || undefined,
    description: [c.address, c.contactPerson, c.phone, c.inn].filter(Boolean).join(' · ') || undefined,
  };
}

function carrierHit(c: Carrier): GlobalSearchHit {
  return {
    id: String(c.id),
    type: 'carrier',
    title: c.name,
    subtitle: c.legalAddress || undefined,
    description: [c.contactPerson, c.phone, c.inn].filter(Boolean).join(' · ') || undefined,
  };
}

function driverHit(d: Driver): GlobalSearchHit {
  return {
    id: String(d.id),
    type: 'driver',
    title: d.fullName,
    subtitle: d.isCompanyDriver ? 'Водитель компании' : 'Внешний водитель',
    description: [d.phone, d.driverLicense].filter(Boolean).join(' · ') || undefined,
  };
}

export interface GlobalSearchApiTriggers {
  getApplicationById: (id: number) => { unwrap: () => Promise<Application> };
  getRouteById: (id: number) => { unwrap: () => Promise<Route> };
  getContractorById: (id: number) => { unwrap: () => Promise<Contractor> };
  getCarrierById: (id: number) => { unwrap: () => Promise<Carrier> };
  getDriverById: (id: number) => { unwrap: () => Promise<Driver> };
  getApplications: (args: { page?: number; limit?: number; search?: string }) => { unwrap: () => Promise<{ applications: Application[] }> };
  getAllRoutes: (args: { page?: number; limit?: number; search?: string }) => { unwrap: () => Promise<{ routes: Route[] }> };
  getAllContractors: () => { unwrap: () => Promise<Contractor[]> };
  getCarriers: () => { unwrap: () => Promise<Carrier[]> };
  getDrivers: () => { unwrap: () => Promise<Driver[]> };
}

async function tryUnwrap<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch {
    return null;
  }
}

/** Поиск по id (только число). */
async function searchByNumericId(id: number, api: GlobalSearchApiTriggers, query: string): Promise<GlobalSearchHit[]> {
  const [app, route, contractor, carrier, driver] = await Promise.all([
    tryUnwrap(() => api.getApplicationById(id).unwrap()),
    tryUnwrap(() => api.getRouteById(id).unwrap()),
    tryUnwrap(() => api.getContractorById(id).unwrap()),
    tryUnwrap(() => api.getCarrierById(id).unwrap()),
    tryUnwrap(() => api.getDriverById(id).unwrap()),
  ]);

  const hits: GlobalSearchHit[] = [];
  if (app) hits.push(appHit(app));
  if (route) hits.push(routeHit(route));
  if (contractor) hits.push(contractorHit(contractor));
  if (carrier) hits.push(carrierHit(carrier));
  if (driver) hits.push(driverHit(driver));

  return finalizeHits(hits, query);
}

function filterApplicationsByText(q: string, apps: Application[]): Application[] {
  const n = q.trim();
  if (!n) return apps;
  return apps.filter(
    (a) =>
      textIn(n, a.internalNumber, a.externalNumber, a.client?.name, a.loadingAddress, a.unloadingAddress, a.loadingCity ?? undefined, a.unloadingCity ?? undefined) ||
      textIn(n, a.shipper?.name, a.consignee?.name, a.expeditor?.name)
  );
}

function filterContractorsByText(q: string, list: Contractor[]): Contractor[] {
  return list.filter((c) => textIn(q, c.name, c.address, c.contactPerson, c.phone, c.inn, c.email ?? undefined));
}

function filterCarriersByText(q: string, list: Carrier[]): Carrier[] {
  return list.filter((c) => textIn(q, c.name, c.legalAddress, c.postalAddress, c.contactPerson, c.phone, c.inn, c.kpp));
}

function filterDriversByText(q: string, list: Driver[]): Driver[] {
  return list.filter((d) => textIn(q, d.fullName, d.phone, d.passportData, d.driverLicense));
}

/** Текстовый поиск и номер заявки вида ГГГГ-xxx. */
async function searchByText(q: string, api: GlobalSearchApiTriggers): Promise<GlobalSearchHit[]> {
  const hits: GlobalSearchHit[] = [];
  const includeRefs = q.trim().length >= MIN_QUERY_LENGTH_FOR_REFERENCES;

  const [appsRes, routesRes, contractors, carriers, drivers] = await Promise.all([
    tryUnwrap(() => api.getApplications({ page: 1, limit: 25, search: q }).unwrap()),
    tryUnwrap(() => api.getAllRoutes({ page: 1, limit: 25, search: q }).unwrap()),
    includeRefs ? tryUnwrap(() => api.getAllContractors().unwrap()) : Promise.resolve(null),
    includeRefs ? tryUnwrap(() => api.getCarriers().unwrap()) : Promise.resolve(null),
    includeRefs ? tryUnwrap(() => api.getDrivers().unwrap()) : Promise.resolve(null),
  ]);

  const appsRaw = appsRes?.applications ?? [];
  const routesRaw = routesRes?.routes ?? [];

  const apps = filterApplicationsByText(q, appsRaw);
  const routes = routesRaw;

  apps.forEach((a) => hits.push(appHit(a)));
  routes.forEach((r) => hits.push(routeHit(r)));

  if (includeRefs) {
    filterContractorsByText(q, contractors ?? []).forEach((c) => hits.push(contractorHit(c)));
    filterCarriersByText(q, carriers ?? []).forEach((c) => hits.push(carrierHit(c)));
    filterDriversByText(q, drivers ?? []).forEach((d) => hits.push(driverHit(d)));
  }

  return finalizeHits(hits, q);
}

/**
 * Собирает результаты глобального поиска без изменения backend.
 */
export async function runGlobalSearch(rawQuery: string, api: GlobalSearchApiTriggers): Promise<GlobalSearchHit[]> {
  const q = rawQuery.trim();
  if (!q) return [];

  if (isNumericIdQuery(q)) {
    const id = parseInt(q, 10);
    if (!Number.isFinite(id)) return [];
    return await searchByNumericId(id, api, q);
  }

  if (isApplicationNumberQuery(q)) {
    const byNumber = await tryUnwrap(() => api.getApplications({ page: 1, limit: 15, search: q }).unwrap());
    const apps = byNumber?.applications ?? [];
    if (apps.length > 0) {
      return finalizeHits(apps.map(appHit), q);
    }
  }

  return await searchByText(q, api);
}

export const GLOBAL_SEARCH_GROUPS: { title: string; types: GlobalSearchEntityType[] }[] = [
  { title: 'Заявки', types: ['application'] },
  { title: 'Маршруты', types: ['route'] },
  { title: 'Контрагенты', types: ['counterparty'] },
  { title: 'Перевозчики', types: ['carrier'] },
  { title: 'Водители', types: ['driver'] },
];

/** Ранжирование внутри типа: префиксное совпадение title с запросом выше, затем localeCompare по title. */
function compareHitsWithinType(a: GlobalSearchHit, b: GlobalSearchHit, queryForRanking: string): number {
  const q = queryForRanking.trim();
  if (q.length > 0) {
    const ql = q.toLowerCase();
    const ta = a.title.toLowerCase();
    const tb = b.title.toLowerCase();
    const prefixA = ta.startsWith(ql) ? 0 : 1;
    const prefixB = tb.startsWith(ql) ? 0 : 1;
    if (prefixA !== prefixB) return prefixA - prefixB;
  }
  return a.title.localeCompare(b.title, 'ru', { sensitivity: 'base' });
}

export function groupGlobalSearchHits(
  hits: GlobalSearchHit[],
  queryForRanking?: string
): { title: string; items: GlobalSearchHit[] }[] {
  const qRank = queryForRanking ?? '';
  const sections: { title: string; items: GlobalSearchHit[] }[] = [];
  for (const g of GLOBAL_SEARCH_GROUPS) {
    const set = new Set(g.types);
    const items = hits.filter((h) => set.has(h.type));
    if (items.length > 0) {
      const sorted = [...items].sort((a, b) => compareHitsWithinType(a, b, qRank));
      sections.push({ title: g.title, items: sorted });
    }
  }
  return sections;
}
